import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { TimerBarTemplate as template } from './timer-bar.template';
import { TimerBarStyles as styles } from './timer-bar.styles';
import { ICommonService } from '../../../services';

const name = 'timer-bar';
@customElement({
  name,
  template,
  styles,
})
export class TimerBar extends FASTElement {
  @ICommonService commonService: ICommonService;
  @attr @observable startBarColor: string = '#C7FF8C';
  @attr @observable stopBarColor: string = '#C0AB42';
  @attr @observable expendedBarColor: string = '#3C3C3C';
  @attr @observable showSeconds: boolean = true;
  @observable public currentMillis: number = 0;
  @observable public startMillis: number = 0;
  @observable public stopMillis: number = 0;
  @observable public startStr: string = '';
  @observable public stopStr: string = '';
  @observable public timeZone: string = '';
  @observable timeRemaining: string = '';
  public regressiveBarBackground: HTMLDivElement;
  public regressiveBarForeground: HTMLDivElement;
  private timeoutMillis = 60;
  private minuteMillis: number = 60 * 1000;
  private hourMillis: number = 60 * this.minuteMillis;
  private timerBarStarted = false;
  private timerInMillisTotal = 0;
  private worker: Worker;

  private parseISOString(dateString: string): number {
    const date = new Date(dateString);
    const dateParsed = Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds()
    );
    return dateParsed;
  }

  connectedCallback() {
    super.connectedCallback();
    this.regressiveBarForeground.style.backgroundColor = this.startBarColor; // For browsers that do not support gradients
    this.regressiveBarForeground.style.backgroundImage =
      'linear-gradient(to right, ' + this.stopBarColor + ', ' + this.startBarColor + ')';
    this.regressiveBarBackground.style.backgroundColor = this.expendedBarColor;
    this.refreshTimeClock(true);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.timerBarStarted = false;
    if (this.worker) {
      this.worker.terminate();
      this.worker = undefined;
    }
  }

  private regressiveBarPercent() {
    if (this.timerInMillisTotal == 0) return 0;
    return (100 * (this.stopMillis - this.currentMillis)) / this.timerInMillisTotal;
  }

  serverStartTime(): string {
    return this.startStr.slice(0, 19).replace('T', ' ');
  }

  serverStopTime(): string {
    return this.stopStr.slice(0, 19).replace('T', ' ');
  }

  public startTimer() {
    if (!this.timerBarStarted) {
      this.timerBarStarted = true;
      this.timerInMillisTotal = this.stopMillis - this.startMillis;
      this.worker = new Worker(new URL('./worker.js', import.meta.url));
      this.worker.onmessage = (message) => {
        if (message.data.current > this.stopMillis) {
          this.currentMillis = this.stopMillis;
        } else {
          this.currentMillis = message.data.current;
        }
        this.regressiveBarForeground.style.width = this.regressiveBarPercent() + '%';
        if (this.currentMillis == this.stopMillis) {
          this.worker.terminate();
        }
      };
      const data = {
        current: this.currentMillis,
        stopMillis: this.stopMillis,
        timeoutMillis: this.timeoutMillis,
      };
      this.worker.postMessage(data);
    }
  }

  formatRemaining(currentMillis: number) {
    let remaining = this.stopMillis - currentMillis;
    const hours = remaining >= this.hourMillis ? (remaining / this.hourMillis) >> 0 : 0;
    remaining = remaining - hours * this.hourMillis;
    const minutes = remaining >= this.minuteMillis ? (remaining / this.minuteMillis) >> 0 : 0;
    return this.showSeconds
      ? `${hours > 9 ? hours : '0' + hours}hr ${minutes > 9 ? minutes : '0' + minutes}m`
      : `${hours > 9 ? hours : '0' + hours}hr ${minutes > 9 ? minutes : '0' + minutes}m ${
          remaining > 9 ? remaining : '0' + remaining
        }s`;
  }
  startTimerInterval() {
    if (this.timerBarStarted == true) {
      setInterval(() => {
        this.refreshTimeClock();
      }, 60000); // Update every minute (60000 milliseconds)
    }
  }

  refreshTimeClock(isFirstPageLoad: boolean = false) {
    this.commonService.getTradeSessionTime().then((response) => {
      if (response.REPLY) {
        this.currentMillis = this.parseISOString(response.REPLY[0]?.SERVER_CURRENT_UTC);
        this.startMillis = this.parseISOString(response.REPLY[0]?.TRADE_SESSION_START_UTC);
        this.stopMillis = this.parseISOString(response.REPLY[0]?.TRADE_SESSION_STOP_UTC);
        this.timeZone = response.REPLY[0]?.SERVER_TIME_ZONE;
        this.startStr = response.REPLY[0]?.TRADE_SESSION_START_UTC;
        this.stopStr = response.REPLY[0]?.TRADE_SESSION_STOP_UTC;
        this.timeRemaining = this.formatRemaining(this.currentMillis);
        if (isFirstPageLoad) {
          this.startTimer();
          this.startTimerInterval();
        }
      }
    });
  }
}
